import { TextKey } from '@mews-commander/enums';
import {
    formattedNumber,
    maxInputTextLength,
    positiveAmount,
    presence,
    ValidationRules,
    WithMessage,
} from '@mews-commander/legacy-form';
import { FormValues } from '../types';
import {
    ABSOLUTE_ADJUSTMENT_FIELD_NAME,
    BASE_PRICE_FIELD_NAME,
    BASE_RATE_KEY_FIELD_NAME,
    NAME_FIELD_NAME,
    RATE_GROUP_KEY_FIELD_NAME,
    RATE_KEY_FIELD_NAME,
    RATE_TYPE_FIELD_NAME,
    RELATIVE_ADJUSTMENT_FIELD_NAME,
    SHORT_NAME_FIELD_NAME,
    NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
    EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
    ORDERING_FIELD_NAME,
} from './fieldNames';
import { FieldName } from './types';

type Constraints = Partial<
    Record<keyof FormValues, ValidationRules & { positiveAmount?: WithMessage; formattedNumber?: WithMessage }>
>;

const getCommonConstraints = (): Constraints => ({
    [NAME_FIELD_NAME]: { presence, length: maxInputTextLength },
    [SHORT_NAME_FIELD_NAME]: { length: maxInputTextLength },
    [RATE_GROUP_KEY_FIELD_NAME]: { presence },
    [RATE_KEY_FIELD_NAME]: {
        length: maxInputTextLength,
    },
    [RATE_TYPE_FIELD_NAME]: { presence },
    [ORDERING_FIELD_NAME]: {
        numericality: {
            message: TextKey.ValidateNumber,
            onlyInteger: true,
        },
    },
});

const baseRateConstraints: Constraints = {
    [BASE_PRICE_FIELD_NAME]: { presence, positiveAmount, formattedNumber },
    [EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME]: { formattedNumber },
    [NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME]: { formattedNumber },
};

const dependantRateConstraints: Constraints = {
    [BASE_RATE_KEY_FIELD_NAME]: { presence },
    [ABSOLUTE_ADJUSTMENT_FIELD_NAME]: {
        presence,
        formattedNumber,
    },
    [RELATIVE_ADJUSTMENT_FIELD_NAME]: {
        presence,
        formattedNumber,
    },
};

interface getConstraintsParams {
    dependent?: boolean;
    enabledFields?: Array<FieldName>;
    unlockedFields?: Array<FieldName>;
}

export const getConstraints = ({ dependent, enabledFields, unlockedFields }: getConstraintsParams) => {
    const constraints = {
        ...getCommonConstraints(),
        ...(dependent ? dependantRateConstraints : baseRateConstraints),
    };

    if (!enabledFields) {
        return constraints;
    }

    return Object.keys(constraints)
        .filter(
            (key) =>
                enabledFields?.includes(key as keyof FormValues) || unlockedFields?.includes(key as keyof FormValues)
        )
        .reduce((obj, key) => {
            obj[key as keyof FormValues] = constraints[key as keyof FormValues];
            return obj;
        }, {} as Constraints);
};
